import React, { useMemo, useState } from 'react';
import LineChart, { STROKES } from './LineChart.jsx';
import { SketchEmptyChart } from '../sketch/index.jsx';
import {
  AGGREGATIONS,
  DEFAULT_AGGREGATION,
  buildSeries,
  collectSessions,
  isSeriesEmpty,
  seriesTrend,
} from '../../utils/evaluation.js';

const AGGREGATION_KEYS = Object.keys(AGGREGATIONS);

/**
 * Signed change, formatted for reading at a glance.
 * @param {{delta: number, percent: number}|null} trend
 */
function TrendNote({ trend }) {
  if (!trend) return <span className="text-ink-soft">—</span>;
  const up = trend.delta > 0;
  const flat = trend.delta === 0;

  return (
    <span className={flat ? 'text-ink-soft' : up ? 'text-done' : 'text-accent'}>
      {up ? '↑' : flat ? '→' : '↓'} {Math.abs(trend.delta)}
      {trend.percent !== 0 && ` (${Math.abs(trend.percent)}%)`}
    </span>
  );
}

/**
 * The evaluation tab: how the recorded numbers have moved over time.
 *
 * Only ever shown for habits created with tracking switched on. Every session
 * of the habit counts, not just the one being logged, so the chart is a
 * history rather than a snapshot.
 *
 * @param {object} habit the sport habit
 * @param {Record<string, object>} sportLogs every log in the app
 */
export default function SportEval({ habit, sportLogs }) {
  const [aggregation, setAggregation] = useState(DEFAULT_AGGREGATION);
  // Lines the reader has switched off in the legend.
  const [hidden, setHidden] = useState([]);

  const series = useMemo(
    () => buildSeries(habit, sportLogs, aggregation),
    [habit, sportLogs, aggregation]
  );
  const sessionCount = collectSessions(habit, sportLogs).length;
  const multiSet = habit.sport.stations.some((station) => station.sets > 1);

  const toggle = (id) =>
    setHidden((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  if (isSeriesEmpty(series)) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-center text-ink-soft">
        <SketchEmptyChart className="text-line" />
        <p className="font-hand text-xl">nothing to show yet</p>
        <p className="max-w-xs font-hand text-lg">
          fill in a session or two and the numbers start drawing themselves
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="font-hand text-xl leading-none">
          {sessionCount} session{sessionCount === 1 ? '' : 's'}
        </p>

        {/* Only worth offering when a station actually has sets to combine */}
        {multiSet && (
          <div className="ml-auto flex gap-1.5">
            {AGGREGATION_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setAggregation(key)}
                aria-pressed={aggregation === key}
                className={`rounded-pill border px-2 py-0.5 text-[10px] uppercase tracking-widest transition-colors ${
                  aggregation === key
                    ? 'border-accent bg-accent text-white'
                    : 'border-line text-ink-soft hover:border-ink'
                }`}
              >
                {AGGREGATIONS[key].label}
              </button>
            ))}
          </div>
        )}
      </div>

      <LineChart series={series} hidden={hidden} />

      {/* Legend doubles as the line filter */}
      <ul className="flex flex-col gap-1">
        {series.map((line, index) => {
          const off = hidden.includes(line.id);
          const empty = line.points.length === 0;

          return (
            <li key={line.id}>
              <button
                type="button"
                onClick={() => !empty && toggle(line.id)}
                aria-pressed={!off}
                disabled={empty}
                className={`flex w-full items-baseline gap-2 py-0.5 text-left transition-opacity ${
                  off || empty ? 'opacity-40' : ''
                }`}
              >
                <span
                  aria-hidden="true"
                  className="mt-1 h-0.5 w-5 shrink-0 rounded-pill"
                  style={{ background: STROKES[index % STROKES.length] }}
                />
                <span className="min-w-0 flex-1 truncate font-hand text-lg leading-tight">
                  {line.label}
                </span>
                <span className="shrink-0 font-hand text-lg leading-tight">
                  {empty ? <span className="text-ink-soft">no data</span> : <TrendNote trend={seriesTrend(line)} />}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
